import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MtnaPopoverConfig } from '@mtna/core-angular';
import { Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { Subject } from 'rxjs';

import {
  RdsVariableSelectionDialogComponent,
  RdsVariableSelectionDialogData,
} from '../variable-selection-dialog/variable-selection-dialog.component';
import { ALL_MEASURE_OPTION_TYPES, MEASURE_OPTIONS, MeasureOptionType } from './measure-option.model';
import { MeasureSelection } from './measure-selection.model';

const noop = () => {
  /* Do nothing */
};

/** Error when invalid control is dirty, touched, or submitted. */
export class RdsMeasureSelectErrorStateMatcher implements ErrorStateMatcher {
  constructor(private _control: NgControl) {}
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(this._control && this._control.invalid && (this._control.dirty || this._control.touched || isSubmitted));
  }
}

/**
 * Select control to select a measure, supports reactive forms.
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-measure-select',
  templateUrl: './measure-select.component.html',
  styleUrls: ['./measure-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsMeasureSelectComponent<V extends Variable | VariableSummary = Variable>
  implements ControlValueAccessor, DoCheck, OnDestroy
{
  static ngAcceptInputType_variables: VariableSummary[] | Variable[] | null | undefined;
  DEFAULT_MEASURES: MeasureSelection<V>[] = [new MeasureSelection<V>('COUNT')];

  /** Popover configuration to describe the control */
  @Input() config: MtnaPopoverConfig = new MtnaPopoverConfig(
    $localize`Measure`,
    $localize`Count/Percentage of records, or statistical aggregation (e.g. sum, average) of numeric variables, rolled up by dimensions, that define the content of the table cell values.`,
    'after'
  );

  /** Whether the control is disabled */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: boolean | string) {
    const newValue = coerceBooleanProperty(value);
    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  /**
   * Whether multiple measures can be selected
   * TODO - implement multi selection
   */
  @Input() multiple = false;
  /** Whether the control is required */
  @Input() required = false;
  /** Label for mat-select, defaults to "Measure" */
  @Input() selectLabel = $localize`Measure`;
  /** Placeholder for mat-select, defaults to "Choose measure..." */
  @Input() selectPlaceholder = $localize`Choose measure...`;
  /** Suffix icon for the select, defaults to null */
  @Input() suffixIcon: string | null = null;
  /** Measure types to select from, defaults to all */
  @Input() types: Array<MeasureOptionType> = [...ALL_MEASURE_OPTION_TYPES];

  /** The selected MeasureSelection and value for this control, if it exists */
  @Input()
  set value(value: MeasureSelection<V>[]) {
    if (value !== this.value) {
      this._value = value;
      this._changeDetectorRef.markForCheck();
    }
  }
  get value(): MeasureSelection<V>[] {
    return this._value;
  }
  private _value: MeasureSelection<V>[] = this.DEFAULT_MEASURES;

  /** Variables to choose from if a measure requires it. */
  @Input()
  get variables(): V[] {
    return this._variables;
  }
  set variables(variables: V[]) {
    this._variables = variables || [];
  }
  private _variables: V[] = [];

  /** Changed value of the form control */
  @Output() readonly selectionChange = new EventEmitter<MeasureSelection<V>[]>();

  /** Internal <mat-select> */
  @ViewChild(MatSelect) matSelect!: MatSelect;

  /** Custom error state matcher */
  _matcher: RdsMeasureSelectErrorStateMatcher | undefined;
  _measureOptions = MEASURE_OPTIONS;

  private _destroyed$ = new Subject<void>();

  /** `View -> model callback called when value changes` */
  _onChange: (value: unknown) => void = noop;

  /** `View -> model callback called when select has been touched` */
  _onTouched = noop;

  constructor(
    @Self() @Optional() public _control: NgControl,
    elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _focusMonitor: FocusMonitor
  ) {
    if (this._control) {
      this._control.valueAccessor = this;
      this._matcher = new RdsMeasureSelectErrorStateMatcher(this._control);
    }
    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  ngDoCheck(): void {
    if (this.matSelect) {
      this.matSelect.updateErrorState();
    }
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Saves a callback function to be invoked when the select's value
   * changes from user input. Part of the ControlValueAccessor interface
   * required to integrate with Angular's core forms API.
   *
   * @param fn Callback to be triggered when the value changes.
   */
  registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  /**
   * Saves a callback function to be invoked when the select is blurred
   * by the user. Part of the ControlValueAccessor interface required
   * to integrate with Angular's core forms API.
   *
   * @param fn Callback to be triggered when the component has been touched.
   */
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: MeasureSelection<V>[]): void {
    this.value = obj;
  }

  _emitChangeEvent(newValue: MeasureSelection<V>[] | null | undefined) {
    // Measure can never be null
    this.value = newValue !== null && newValue !== undefined ? newValue : this.DEFAULT_MEASURES;
    this._onChange(this.value);
    this.selectionChange.emit(this.value);
  }

  /**
   * Manually handle when options are selected/unselected.
   * @param event option selection event
   */
  _handleOptionSelectionChange(type: MeasureOptionType) {
    if (this._measureOptions[type].requiresVariable) {
      // If the user is required to select a variable for the measure, open the dialog
      const ref = this._dialog.open(RdsVariableSelectionDialogComponent, {
        data: new RdsVariableSelectionDialogData<V>(this.variables),
        disableClose: true,
        width: '700px',
      });
      ref.afterClosed().subscribe((output: V) => {
        if (output) {
          if (!this.value || !this.value.some((m) => m.measureType === type)) {
            // If a new type is being added
            const oldValues = this.value ? this.value : [];
            const newValues: MeasureSelection<V>[] = this.multiple
              ? [...oldValues, new MeasureSelection<V>(type, output)]
              : [new MeasureSelection<V>(type, output)];

            this._emitChangeEvent(newValues);
            this._changeDetectorRef.markForCheck();
          }
          const measureWithSameType = this.value ? this.value.find((m) => m.measureType === type) : null;
          if (!!measureWithSameType && measureWithSameType.variable?.id !== output.id) {
            // If the type already existed but the variable changed
            this._emitChangeEvent(this.value.map((m) => (m.measureType === type ? new MeasureSelection(type, output) : m)));
            this._changeDetectorRef.markForCheck();
          }
        } else {
          // Reset control if the dialog is cancelled;
          this._emitChangeEvent(this.DEFAULT_MEASURES);
        }
      });
    } else if (!this.value || !this.value.some((m) => m.measureType === type)) {
      // If the option was selected and not already selected, set it
      const oldValues = this.value ? this.value : [];
      const newValues: MeasureSelection<V>[] = this.multiple
        ? [...oldValues, new MeasureSelection<V>(type)]
        : [new MeasureSelection<V>(type)];
      this._emitChangeEvent(newValues);
    }
  }

  _unselect(event: MatOptionSelectionChange) {
    if (event.isUserInput) {
      this._emitChangeEvent(this.DEFAULT_MEASURES);
    }
  }
}
